import i18n from "../i18n";
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './Clients.css';
import config from '../config';
import moment from 'moment';
import ClientsHistory from "./ClientsHistory";

const tr = (...args) => i18n.t(...args);

const Clients = () => {

  // --- Inline form (unchanged, just tidy) ---
  const ClientForm = ({ client, onSave }) => {

    const [form, setForm] = useState({
      lastname: '',
      firstname: '',
      phone: '',
      email: '',
      comment: '',
      rate: 3,
    });

    useEffect(() => {
      if (client) {
        setForm({
          ...client,
          rate: client.rate ?? 3,
        });
      }
    }, [client]);

    const handleChange = (e) => {
      const { name, value } = e.target;
      if (name === 'rate') {
        const n = Math.min(5, Math.max(1, parseInt(value, 10) || 3));
        setForm({ ...form, rate: n });
      } else {
        setForm({ ...form, [name]: value });
      }
    };

    return (
      <form className="client-form" onSubmit={e => { e.preventDefault(); onSave(form); }}>
        <div className="cf-section">{tr('Client Info:')}</div>
        <div className="cf-row">
          <input name="lastname" value={form.lastname} onChange={handleChange} placeholder={tr('Last name')} />
          <input name="firstname" value={form.firstname} onChange={handleChange} placeholder={tr('First name')} />
        </div>
        <div className="cf-section" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {tr('Contact Details:')}
          <button type="button" className="rlToolbarBtn" style={{ padding: '3px 8px', fontSize: 12, marginLeft: 'auto' }}
            title={tr('Pull the caller of the last phone call')}
            onClick={async () => {
              try {
                const { data } = await axios.get(`${config.API_BASE_URL}/phone/last-call`);
                const call = data?.call;
                if (!call) { alert(tr(data?.configured ? 'No recent call.' : 'Caller ID is not set up. Ask your administrator (Settings → Phone).')); return; }
                if (call.client) setForm({ ...call.client, rate: call.client.rate ?? 3, email: call.client.email || '', comment: call.client.comment || '' });
                else setForm((f) => ({ ...f, phone: call.number }));
                axios.post(`${config.API_BASE_URL}/phone/calls/${call.id}/used`).catch(() => {});
              } catch { alert(tr('Something went wrong — please try again.')); }
            }}>
            📞 {tr('Last call')}
          </button>
        </div>
        <div className="cf-row">
          <input name="phone" value={form.phone} onChange={handleChange} placeholder={tr('Phone')} />
          <input name="email" value={form.email} onChange={handleChange} placeholder={tr('Email')} />
        </div>
        <div className="cf-section">{tr('Comments:')}</div>
        <textarea name="comment" value={form.comment} onChange={handleChange} rows={3} />
        <div className="cf-rating">
          <span className="cf-section" style={{ margin: 0 }}>{tr('Rating:')}</span>
          {[1, 2, 3, 4, 5].map((n) => {
            const current = form.rate ?? 3;
            const filled = n <= current;

            return (
              <span
                key={n}
                onClick={() => setForm({ ...form, rate: n })}
                style={{
                  cursor: 'pointer',
                  fontSize: 24,
                  color: filled ? '#f5b301' : '#d1d5db',
                  userSelect: 'none',
                  transition: 'transform .1s ease',
                }}
                title={tr('{{n}} star{{var2}}', {
                  n,
                  var2: n > 1 ? 's' : ''
                })}
              >
                {filled ? '★' : '☆'}
              </span>
            );
          })}
        </div>
        <div className="cf-actions">
          <button type="submit" className="cf-save">{tr('Save')}</button>
        </div>
      </form>
    );
  };

  // --- State ---
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyClient, setHistoryClient] = useState(null);

  const [loading, setLoading] = useState(true);       // initial page
  const [loadingMore, setLoadingMore] = useState(false); // background pages
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [filterDate, setFilterDate] = useState('');
  const [tables, setTables] = useState([]);
  const [filterTable, setFilterTable] = useState(''); // table id
  const [orderBy, setOrderBy] = useState('reservations'); // reservations|surname|name|phone
  const [orderDir, setOrderDir] = useState('desc');       // asc|desc
  const [serverFiltered, setServerFiltered] = useState(false);
  
  const ignorePrefetchRef = useRef(false);

  const LIMIT = 100;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(tr('{{API_BASE_URL}}/tables', {
          API_BASE_URL: config.API_BASE_URL
        }));
        setTables(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to load tables', e);
      }
    })();
  }, []);

  const applyServerSearch = async () => {
    ignorePrefetchRef.current = true;
    setClients([]);
    setLoading(true);
    setHasMore(false); // server returns the full set
    try {
      const term = search && search.trim().length >= 3 ? search.trim() : '-'; // path must be non-empty
      const { data } = await axios.get(tr('{{API_BASE_URL}}/clients/search/{{var2}}', {
        API_BASE_URL: config.API_BASE_URL,
        var2: encodeURIComponent(term)
      }) , {
        params: {
          date: filterDate || undefined,
          table: filterTable || undefined,
          order: orderBy,
          dir: orderDir
        }
      });
      setClients(Array.isArray(data) ? data : []);
      setOffset(0);
      setServerFiltered(true);
    } catch (e) {
      console.error('search error', e);
      alert('Could not fetch clients for the selected filters.');
    } finally {
      setLoading(false);
    }
  };

  const clearServerSearch = () => {
    setFilterDate('');
    setFilterTable('');
    setOrderBy('reservations');
    setOrderDir('desc');
    setServerFiltered(false);
    setSearch('');
    ignorePrefetchRef.current = false;
    prefetchAllClients();
  };

  const loadClients = async ({ reset = false, offsetArg = null } = {}) => {
    const explicitOffset = offsetArg !== null;           // <-- caller-driven paging
    const useOffset = explicitOffset ? offsetArg : (reset ? 0 : offset);

    // local flag so we only flip loadingMore if we set it
    let setLoadingMoreHere = false;

    if (reset) {
      setLoading(true);
      setHasMore(true);
      setClients([]);
      // do NOT touch loadingMore here
    } else {
      // If we’re doing an explicit page (prefetch loop), DO NOT guard or set loadingMore.
      if (!explicitOffset) {
        if (loading || loadingMore || !hasMore) {
          return { hasMore: false, nextOffset: useOffset, got: 0 };
        }
        setLoadingMore(true);
        setLoadingMoreHere = true;
      }
    }

    try {
      const { data } = await axios.get(tr('{{API_BASE_URL}}/clients', {
        API_BASE_URL: config.API_BASE_URL
      }), {
        params: { limit: LIMIT, offset: useOffset }
      });
  
      const batch = Array.isArray(data) ? data : [];
      const got = batch.length;
      const nextOffset = useOffset + got;
      const more = got === LIMIT;
  
      // If a server search is active, ignore background prefetch updates
      if (ignorePrefetchRef.current) {
        return { hasMore: false, nextOffset: useOffset, got: 0 };
      }
      setClients(prev => (reset ? batch : [...prev, ...batch]));
      setOffset(nextOffset);
      setHasMore(more);
  
      return { hasMore: more, nextOffset, got };
    } catch (e) {
      console.error('[clients] fetch error:', e);
      setHasMore(false);
      return { hasMore: false, nextOffset: useOffset, got: 0 };
    } finally {
      if (reset) setLoading(false);
      if (setLoadingMoreHere) setLoadingMore(false);      // only if we set it here
    }
  };

  const prefetchAllClients = async () => {
    let { hasMore: more, nextOffset } = await loadClients({ reset: true, offsetArg: 0 });
  
    while (more) {
      await new Promise(r => setTimeout(r, 120));
      const res = await loadClients({ offsetArg: nextOffset }); // explicit => won’t be skipped
      nextOffset = res.nextOffset;
      more = res.hasMore;
    }
  };

  useEffect(() => {
    prefetchAllClients();
  }, []);

  // --- Actions ---

  const handleDelete = async (client) => {
    if (window.confirm(tr('Are you sure you want to delete {{firstname}} {{lastname}}?', {
      firstname: client.firstname,
      lastname: client.lastname
    }))) {
      await axios.delete(tr('{{API_BASE_URL}}/clients/{{id}}', {
        API_BASE_URL: config.API_BASE_URL,
        id: client.id
      }));
      prefetchAllClients(); // reload everything
    }
  };

  const saveClient = async (clientData) => {
    if (clientData.id) {
      await axios.put(tr('{{API_BASE_URL}}/clients/{{id}}', {
        API_BASE_URL: config.API_BASE_URL,
        id: clientData.id
      }), clientData);
    } else {
      await axios.post(tr('{{API_BASE_URL}}/clients', {
        API_BASE_URL: config.API_BASE_URL
      }), clientData);
    }
    setModalOpen(false);
    prefetchAllClients(); // reload everything
  };

  // --- Filtering ---
  const filteredClients = clients.filter(c => {
    if (serverFiltered) return true; // do not client-filter server results
    const term = search.toLowerCase();
    return [c.firstname, c.lastname, c.phone, c.email, c.comment]
      .some(field => field?.toLowerCase().includes(term));
  });

  const renderStars = (rate) => {
    return '★'.repeat(rate) + '☆'.repeat(5 - rate);
  };

  // --- Render ---
  return (
    <div className="clients-page" style={{ margin: '30px' }}>
      <h2>{tr('Clients Management')}</h2>
      <div className="clients-toolbar">

        <div className="clients-toolbar-row">
          <input
            className="clients-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tr('Search clients by lastname, firstname, phone or email')}
          />
          <button onClick={applyServerSearch} style={{ fontWeight: 700 }}>{tr('Search')}</button>
          <button onClick={clearServerSearch}>{tr('Clear')}</button>
          <button
            className="clients-add"
            onClick={() => { setEditClient(null); setModalOpen(true); }}
          >
            + {tr('Add New Client')}
          </button>
        </div>
        <div className="clients-toolbar-row">
          <label style={{ fontWeight: 600 }}>
            {tr('Search client by reservation`s:')}{' '}{tr('Date:')}<input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} style={{ marginLeft: 6 }} />
          </label>

          <label>{tr('Table:')}<select disabled={!filterDate} value={filterTable} onChange={(e) => setFilterTable(e.target.value)} style={{ marginLeft: 6 }}>
              <option value="">{tr('All')}</option>
              {tables.map(t => (
                <option key={t.id} value={t.id}>{t.number}</option>
              ))}
            </select>
          </label>
          <span className="clients-sep" aria-hidden="true" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>{tr('Order by:')}</span>
        
            {['reservations','firstname','lastname','phone'].map(key => (
              <label key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="radio"
                  name="orderBy"
                  value={key}
                  checked={orderBy === key}
                  onChange={() => {
                    setOrderBy(key);
                    if (key === 'reservations' && orderDir === 'asc') setOrderDir('desc');
                  }}
                />
                {tr(key)}
              </label>
            ))}
        
            <button
              type="button"
              title={tr('Direction: {{var1}}', {
                var1: orderDir.toUpperCase()
              })}
              onClick={() => setOrderDir(d => d === 'asc' ? 'desc' : 'asc')}
              style={{ border: '1px solid #ccc', background: 'white', padding: '3px 5px', borderRadius: 17 }}
            >
              {/* two-state icon */}
              <span style={{ fontSize: 16, color: 'darkBlue' }}>{orderDir === 'asc' ? '▲' : '▼'}</span>
            </button>
          </div>
          <span className="clients-spacer" aria-hidden="true" />
          <span className="results-count">{filteredClients.length}{' '}{tr('clients found')}</span>
          {serverFiltered && (
            <small style={{ opacity: 0.75 }}>{tr('(filtered')}{filterDate ? tr('on {{filterDate}}', {
              filterDate
            }) : ''}{filterTable ? tr(', table #{{var1}}', {
              var1: tables.find(t => String(t.id) === String(filterTable))?.number
            }) : ''}; {orderBy} {orderDir})
            </small>
          )}
        </div>
      </div>
      <div className="clients-table-container">
        <table cellPadding="8" style={{ width: '99%', textAlign: 'center' }}>
          <thead>
            <tr>
              <th style={{width: '30px'}}>#</th>
              <th className="no-wrap">{tr('Name')}</th>
              <th style={{width: '80px'}}>{tr('Rate')}</th>
              <th>{tr('Phone')}</th>
              <th>{tr('Comment')}</th>        
              <th style={{width: '50px'}}>{tr('History')}</th>
              <th style={{width: '50px'}}>{tr('Edit')}</th>
              <th style={{width: '50px'}}>{tr('Delete')}</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center' }}>
                  <h3>{tr('Loading clients...')}</h3>
                </td>
              </tr>
            ) : filteredClients.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center' }}>
                  <h3>{tr('No clients found.')}</h3>
                </td>
              </tr>
            ) : (
              <>
                {filteredClients.map((client, index) => {
                  return (
                    <tr key={client.id} className="client-row">
                      <td><b>{index + 1}</b></td>
                      <td className="no-wrap">{client.lastname} {client.firstname}</td>
                      <td>
                        <span style={{color: 'darkOrange'}} title={tr('Rating: {{var1}}', {
                          var1: client.rate ?? 3
                        })}>
                          {renderStars(client.rate)}
                        </span>
                      </td>
                      <td><a href={`tel:${client.phone}`}>{client.phone}</a></td>
                      <td><i>{client.comment}</i></td>
                      <td>
                        <button
                          style={{ color: 'darkBlue', margin: '5px', background: 'none', border: 'none' }}
                          title={tr('See reservation history for this client')}
                          onClick={() => {
                            const name =
                              [client.lastname, client.firstname].filter(Boolean).join(" ").trim() ||
                              client.name ||
                              "";
                            setHistoryClient({ id: client.id, name });
                            setHistoryOpen(true);
                          }}
                        >{tr('📜')}</button>
                      </td>
                      <td>
                        <button
                          style={{ color: 'darkGreen', margin: '5px', background: 'none', border: 'none' }}
                          title={tr('Edit this client')}
                          onClick={() => { setEditClient(client); setModalOpen(true); }}
                        >{tr('✎')}</button>
                      </td>
                      <td>
                        <button
                          style={{ color: 'darkRed', margin: '5px', background: 'none', border: 'none' }}
                          title={tr('Delete this client')}
                          onClick={() => handleDelete(client)}
                        >{tr('🗑')}</button>
                      </td>
                    </tr>
                  );
                })}

                {loadingMore && (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center' }}>
                      <div>{tr('Loading more...')}</div>
                    </td>
                  </tr>
                )}

                {!hasMore && clients.length > 0 && (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: 10 }}>
                      <small>{tr('End of list')}</small>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>

        {/* Viewport-centered overlay spinner:
            - shows during first load
            - and while background pages are still loading */}
        {(loading || (clients.length > 0 && hasMore)) && (
          <div className="viewport-overlay">
            <div className="loader loader-overlay"></div>
          </div>
        )}
      </div>
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-body" style={{ width: '360px' }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" aria-label={tr('Close')} onClick={() => setModalOpen(false)}>×</button>
            <ClientForm
              client={editClient}
              onSave={saveClient}
            />
          </div>
        </div>
      )}
      
      <ClientsHistory
        clientId={historyClient?.id}
        clientName={historyClient?.name || ""}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        prefetch={false}
      />
      
    </div>
  );
};

export default Clients;
